(() => {
  if (customElements.get('mh-wayfinding')) return;
  class MarbilyWayfinding extends HTMLElement {
    connectedCallback() {
      this.route = this.querySelector('[data-route]');
      this.camera = this.querySelector('[data-camera]');
      this.vehicle = this.querySelector('[data-map-vehicle]');
      this.pauseButton = this.querySelector('[data-map-pause]');
      this.replayButton = this.querySelector('[data-map-replay]');
      this.overviewButton = this.querySelector('[data-map-overview]');
      this.step = this.querySelector('[data-map-step]');
      if (!this.route || !this.camera) return;
      this.length = this.route.getTotalLength();
      this.elapsed = this.elapsed || 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.pauseButton?.addEventListener('click', () => {
        if (this.dataset.state === 'playing') this.pause(false);
        else this.play();
      }, options);
      this.replayButton?.addEventListener('click', () => {
        if (this.motion.matches) return this.overview();
        this.elapsed = 0;
        this.render(0);
        this.play();
      }, options);
      this.overviewButton?.addEventListener('click', () => this.overview(), options);
      document.addEventListener('visibilitychange', () => this.checkVisibility(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.overview();
        this.setControls();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio > .15;
        this.checkVisibility();
      }, { threshold: [0, .15, .4] });
      this.overview();
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.pause(true);
      this.observer?.disconnect();
      this.events?.abort();
    }
    checkVisibility() {
      const available = this.visible && !document.hidden && this.isConnected;
      if (!available && this.dataset.state === 'playing') this.pause(true);
      else if (available && !this.motion.matches && (!this.hasStarted || this.resumeWhenVisible)) {
        this.play();
      }
    }
    setControls() {
      if (!this.pauseButton) return;
      const reduced = this.motion.matches;
      this.pauseButton.hidden = reduced || !['playing', 'paused'].includes(this.dataset.state);
      this.pauseButton.textContent = this.dataset.state === 'playing' ? 'Ⅱ' : '▷';
      this.pauseButton.setAttribute('aria-label', this.dataset.state === 'playing' ? 'Wstrzymaj animację' : 'Wznów animację');

    }
    play() {
      if (this.motion.matches || !this.isConnected) return;
      cancelAnimationFrame(this.frame);
      this.hasStarted = true;
      this.resumeWhenVisible = false;
      this.dataset.state = 'playing';
      this.previousTime = null;
      this.setControls();
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    pause(automatic) {
      cancelAnimationFrame(this.frame);
      this.previousTime = null;
      if (this.dataset.state === 'playing') {
        this.dataset.state = 'paused';
        this.resumeWhenVisible = automatic;
      }
      this.setControls();
    }
    overview() {
      cancelAnimationFrame(this.frame);
      this.resumeWhenVisible = false;
      // Choosing overview should not trigger a new automatic play on the next scroll.
      if (this.dataset.state === 'playing' || this.dataset.state === 'paused') this.hasStarted = true;
      this.dataset.state = 'overview';
      this.elapsed = 0;
      this.dataset.progress = '0';
      this.camera.setAttribute('transform', 'matrix(1 0 0 1 0 0)');
      this.route.style.strokeDasharray = '';
      this.route.style.strokeDashoffset = '';
      this.route.setAttribute('stroke-width', '3.5');
      this.vehicle.setAttribute('visibility', 'hidden');
      this.step.textContent = 'Hala 2 · A19';
      this.setControls();
    }
    tick(time) {
      if (this.dataset.state !== 'playing' || !this.isConnected) return;
      if (this.previousTime !== null) this.elapsed += Math.min(time - this.previousTime, 80);
      this.previousTime = time;
      this.render(this.elapsed / 12000);
      if (this.elapsed >= 12000) {
        this.dataset.state = 'complete';
        this.vehicle.setAttribute('visibility', 'hidden');
        this.setControls();
      } else this.frame = requestAnimationFrame(next => this.tick(next));
    }
    render(progress) {
      const p = Math.min(1, Math.max(0, progress));
      const smooth = x => x * x * (3 - 2 * x);
      const clamp = x => Math.max(0, Math.min(1, x));
      // First establish the street, then track the drive, finally approach the entrance.
      const establish = smooth(clamp(p / .18));
      const arrival = smooth(clamp((p - .73) / .27));
      const zoom = 1 + .16 * establish + 1.04 * arrival;
      const cx = 500 + 36 * establish + 70 * arrival;
      const cy = 360 + 105 * establish + 10 * arrival;
      this.camera.setAttribute('transform', `matrix(${zoom.toFixed(4)} 0 0 ${zoom.toFixed(4)} ${(500 - cx * zoom).toFixed(3)} ${(360 - cy * zoom).toFixed(3)})`);
      this.route.setAttribute('stroke-width', String(3.5 / zoom));
      const drive = smooth(clamp((p - .09) / .78));
      this.route.style.strokeDasharray = `${this.length} ${this.length}`;
      this.route.style.strokeDashoffset = String(this.length * (1 - drive));
      const point = this.route.getPointAtLength(this.length * drive);
      this.vehicle.setAttribute('cx', point.x);
      this.vehicle.setAttribute('cy', point.y);
      this.vehicle.setAttribute('visibility', p > .09 && p < .95 ? 'visible' : 'hidden');
      this.dataset.progress = p.toFixed(4);
      const label = p < .46 ? 'Od ulicy Nadrzecznej' : p < .73 ? 'Wjazd do kompleksu' : 'Wejście · Hala 2, A19';
      if (this.step.textContent !== label) this.step.textContent = label;
    }
  }
  customElements.define('mh-wayfinding', MarbilyWayfinding);
})();
